import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { publicApi, fileUrl } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import InquiryDialog from "@/components/public/InquiryDialog";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { PUBLIC_STATUS_STYLE, PUBLIC_STATUS_LABEL } from "@/components/public/WatchCard";
import { ShieldCheck, ArrowLeft, Watch as WatchIcon } from "lucide-react";

function Spec({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-2.5 border-b border-[var(--border-lch)]">
      <dt className="text-[var(--muted)] text-sm">{label}</dt>
      <dd className="text-[var(--text)] text-sm text-right">{value}</dd>
    </div>
  );
}

export default function WatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [watch, setWatch] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/watches/${id}`)
      .then((r) => { setWatch(r.data); setActive(0); })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-5 py-28 text-center">
          <h1 className="text-2xl font-semibold text-[var(--text)]">This piece isn't available.</h1>
          <p className="text-[var(--text-2)] mt-3">It may be sold, unavailable, or part of a private drop.</p>
          <Button onClick={() => navigate("/watches")} className="mt-6 bg-[#c9a86a] text-[var(--on-gold)] rounded-none">Browse the Collection</Button>
        </div>
      </PublicLayout>
    );
  }
  if (!watch) return <PublicLayout><div className="text-center text-[var(--muted)] py-32">Loading…</div></PublicLayout>;

  const photos = watch.public_photos || [];
  const authPassed = watch.authentication_status === "passed";

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-5 py-10">
        <Link to="/watches" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[#c9a86a] mb-6"><ArrowLeft size={16} /> Back to Collection</Link>
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-[var(--panel-2)] rounded-lg overflow-hidden border border-[var(--border-lch)] flex items-center justify-center">
              {photos[active] ? <img src={fileUrl(photos[active].url)} alt={watch.model} className="w-full h-full object-cover" data-testid="watch-main-photo" />
                : <WatchIcon size={60} className="text-[var(--muted)]" />}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-3 mt-3">
                {photos.map((p, i) => (
                  <button key={p.id || i} onClick={() => setActive(i)} className={`h-16 w-16 rounded border overflow-hidden ${i === active ? "border-[#c9a86a]" : "border-[var(--border-lch)]"}`}>
                    <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {watch.video_url && (
              <div className="mt-4 text-sm"><a href={watch.video_url} target="_blank" rel="noreferrer" className="text-[#c9a86a] hover:text-[#b89455] transition-colors">View video / 360° →</a></div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-[10px] px-2 py-1 rounded-full border tracking-wide ${PUBLIC_STATUS_STYLE[watch.status] || ""}`}>{PUBLIC_STATUS_LABEL[watch.status] || watch.status}</span>
              {authPassed && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/15 text-emerald-300"><ShieldCheck size={12} /> Authenticated</span>}
            </div>
            <div className="text-[var(--muted)] tracking-wide uppercase text-sm">{watch.brand}</div>
            <h1 className="text-3xl font-semibold text-[var(--text)] mt-1">{watch.model}</h1>
            <div className="text-[var(--text-2)] mt-1">{watch.reference_number}{watch.year ? ` · ${watch.year}` : ""}</div>

            <div className="mt-6 text-2xl text-[#c9a86a] font-medium">
              {watch.price_on_request ? <span className="italic text-lg text-[var(--text-2)]">Price on Request</span> : money(watch.price)}
            </div>
            {watch.deposit_required && (
              <div className="mt-2 text-sm text-[var(--text-2)]">Deposit interest (not live yet) - inquiry only, does not reserve{watch.deposit_amount ? `: ${money(watch.deposit_amount)}` : ""}.</div>
            )}

            {watch.public_description && <p className="text-[var(--text-2)] mt-5 leading-relaxed">{watch.public_description}</p>}

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <InquiryDialog watch={watch} label="Request This Watch" />
              {watch.deposit_required
                ? <InquiryDialog watch={watch} label="Inquire about this watch" depositIntent variant="outline" />
                : <Link to="/vip"><Button className="w-full border border-[#c9a86a] bg-transparent text-[#c9a86a] hover:bg-[#c9a86a] hover:text-[var(--on-gold)] rounded-none">Join the VIP List</Button></Link>}
            </div>

            <dl className="mt-8">
              <Spec label="Reference" value={watch.reference_number} />
              <Spec label="Year" value={watch.year} />
              <Spec label="Case Size" value={watch.case_size} />
              <Spec label="Material" value={watch.material} />
              <Spec label="Movement" value={watch.movement} />
              <Spec label="Dial" value={watch.dial} />
              <Spec label="Bracelet / Strap" value={watch.bracelet_strap} />
              <Spec label="Condition" value={watch.condition} />
              <Spec label="Box & Papers" value={watch.box_papers} />
              <Spec label="Service History" value={watch.service_history} />
            </dl>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
